// Parses the backend's 402 QuotaErrorResponse so the UI can prompt the user.
// Copy resolution: account-status reasons (not_entitled / subscription_missing)
// win first — a lapsed subscriber must be told to resubscribe, never to
// "upgrade", even if a usage dimension rode along. Otherwise key off the
// optional `dimension`, then the over-quota reason, then a generic message.
// Nothing here is exhaustive over the vendored union: a future reason/dimension
// value must degrade to the generic prompt rather than break FE typecheck.

import type { QuotaErrorReason } from "../types"

export type { QuotaErrorReason }

export const QUOTA_EXCEEDED_CODE = "QUOTA_EXCEEDED"

// Reasons we render bespoke copy for today, matched as optional known values.
// An unrecognized reason (e.g. `usage_limit_exceeded`, added in backend PR3)
// simply isn't in these sets and falls through to the generic branch.
const RESUBSCRIBE_REASONS: ReadonlySet<string> = new Set([
  "not_entitled",
  "subscription_missing",
])
const OVER_QUOTA_REASONS: ReadonlySet<string> = new Set([
  "voice_minutes_exceeded",
])

export interface QuotaError {
  reason: string | null
  dimension: string | null
}

export interface QuotaErrorCopy {
  title: string
  description: string
}

const OVER_QUOTA_COPY: QuotaErrorCopy = {
  title: "Out of simulation minutes",
  description:
    "You've used all your simulation minutes for this billing period. Upgrade your plan to run more.",
}

const MONITORED_CALLS_COPY: QuotaErrorCopy = {
  title: "Out of conversation uploads",
  description:
    "You've used all your conversation uploads for this billing period. Upgrade your plan to upload more.",
}

const RESUBSCRIBE_COPY: QuotaErrorCopy = {
  title: "Subscription required",
  description:
    "Your subscription is no longer active. Resubscribe to keep running simulations.",
}

const GENERIC_COPY: QuotaErrorCopy = {
  title: "Usage limit reached",
  description:
    "You've reached a usage limit on your current plan. Upgrade your plan to keep going.",
}

const METRICS_PER_CALL_COPY: QuotaErrorCopy = {
  title: "Too many metrics for this run",
  description:
    "This run requests more metrics than your plan allows per call. Reduce the number of metrics, or upgrade your plan for a higher limit.",
}

// Per-dimension copy. Unmapped dimensions (metric_evals, …) fall through to
// GENERIC_COPY, so the backend can add them without an FE change.
const DIMENSION_COPY: Readonly<Record<string, QuotaErrorCopy>> = {
  voice_minutes: OVER_QUOTA_COPY,
  monitored_calls: MONITORED_CALLS_COPY,
  metrics_per_call: METRICS_PER_CALL_COPY,
}

function statusOf(error: unknown): number | null {
  if (typeof error !== "object" || error === null) {
    return null
  }
  const record = error as Record<string, unknown>
  if (typeof record.status === "number") {
    return record.status
  }
  if (typeof record.statusCode === "number") {
    return record.statusCode
  }
  return null
}

function rawBodyOf(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null) {
    return undefined
  }
  const record = error as Record<string, unknown>
  return typeof record.responseBody === "string"
    ? record.responseBody
    : undefined
}

function parseEnvelope(body: string | undefined): {
  code?: string
  reason?: string
  dimension?: string
} {
  if (!body) {
    return {}
  }
  try {
    const parsed = JSON.parse(body) as unknown
    if (typeof parsed !== "object" || parsed === null || !("error" in parsed)) {
      return {}
    }
    const error = (parsed as { error: unknown }).error
    if (typeof error !== "object" || error === null) {
      return {}
    }
    const record = error as Record<string, unknown>
    return {
      code: typeof record.code === "string" ? record.code : undefined,
      reason: typeof record.reason === "string" ? record.reason : undefined,
      dimension:
        typeof record.dimension === "string" ? record.dimension : undefined,
    }
  } catch {
    return {}
  }
}

// A 402 is itself the quota signal; reason/dimension may be absent, but a
// non-quota code rules it out. Unknown reason/dimension values are preserved as
// raw strings — copy resolution, not parsing, decides how to render them.
export function parseQuotaError(error: unknown): QuotaError | null {
  if (statusOf(error) !== 402) {
    return null
  }
  const envelope = parseEnvelope(rawBodyOf(error))
  if (envelope.code && envelope.code !== QUOTA_EXCEEDED_CODE) {
    return null
  }
  return {
    reason: envelope.reason ?? null,
    dimension: envelope.dimension ?? null,
  }
}

// Account-status reasons win over any dimension so a lapsed subscriber is always
// told to resubscribe, not to upgrade. Then dimension-based copy, then the
// over-quota reason, then a generic message.
export function quotaErrorCopy(quota: QuotaError): QuotaErrorCopy {
  if (quota.reason && RESUBSCRIBE_REASONS.has(quota.reason)) {
    return RESUBSCRIBE_COPY
  }
  if (quota.dimension) {
    const byDimension = DIMENSION_COPY[quota.dimension]
    if (byDimension) {
      return byDimension
    }
  }
  if (quota.reason && OVER_QUOTA_REASONS.has(quota.reason)) {
    return OVER_QUOTA_COPY
  }
  return GENERIC_COPY
}

export function quotaErrorMessage(error: unknown): string | null {
  const quota = parseQuotaError(error)
  return quota ? quotaErrorCopy(quota).description : null
}
